import { motion } from 'framer-motion';
import { Bot, Camera, FileText, GraduationCap, BadgeCheck, Ticket, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Bot,
  Camera,
  FileText,
  GraduationCap,
  BadgeCheck,
  Ticket,
};

export default function BusinessScopeSection() {
  const siteConfig = useSiteConfig();
  const { businessScope, contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState(businessScope.ctaText);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, businessScope.whatsappMessage);
  };

  return (
    <section id="business-scope" className="py-20 md:py-28 bg-deep-black relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="text-neon-purple text-sm font-semibold tracking-widest uppercase mb-2">
            {businessScope.sectionTitle}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {businessScope.subtitle}
          </h2>
          <p className="text-gray-500 text-sm">{businessScope.companyName}</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {businessScope.items.map((item, index) => {
            const Icon = iconMap[item.icon] || FileText;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="neon-card rounded-2xl p-5 flex items-center gap-3"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-neon-purple/10 flex items-center justify-center border border-neon-purple/30">
                  <Icon size={18} className="text-neon-purple" />
                </div>
                <span className="text-white text-sm md:text-base font-medium">{item.label}</span>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <button
            onClick={handleCta}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-neon-purple to-neon-pink text-white font-bold rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
